import React, { useState, useEffect, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTwitter, FaTiktok, FaInstagram, FaCheck, FaMagic, FaShieldAlt, FaExchangeAlt, FaGlobe } from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import OrgManager from '../services/OrgManager';
import { useOrg } from '../hooks/useOrg';
import { useTheme } from '../theme/ThemeContext';

let T = {};

const NewListScreen = ({ onClose, isModal, ...props }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const { theme } = useTheme();
    const isRTL = i18n.language === 'he';
    const state = location.state || {};

    T = {
        bg:        theme.bg,
        card:      theme.bgCard,
        surface:   theme.bgSurface,
        border:    theme.border,
        text:      theme.textPrimary,
        textSec:   theme.textSecondary,
        textMuted: theme.textMuted,
        primary:   '#4DA6FF',
    };

    const isForEditing = props.isForEditing ?? state.isForEditing ?? false;
    const listId = props.listId || state.listId;
    const existingListName = props.existingListName || state.existingListName || '';
    const existingListPrompt = props.existingListPrompt || state.existingListPrompt || '';
    const existingListReactionStyle = props.existingListReactionStyle || state.existingListReactionStyle || '';
    const existingIsWideMonitoring = props.existingIsWideMonitoring ?? state.existingIsWideMonitoring ?? false;
    const existingIsWideMonitoringTiktok = props.existingIsWideMonitoringTiktok ?? state.existingIsWideMonitoringTiktok ?? false;
    const existingIsWideMonitoringInstagram = props.existingIsWideMonitoringInstagram ?? state.existingIsWideMonitoringInstagram ?? false;
    const existingVisibility = props.existingVisibility || state.existingVisibility || 'org';
    const existingListRole = props.existingListRole || state.existingListRole || 'general';

    const [title, setTitle] = useState(existingListName);
    const [prompt, setPrompt] = useState(existingListPrompt);
    const [reactionStyle, setReactionStyle] = useState(existingListReactionStyle);
    const [suggestedLists, setSuggestedLists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isWideMonitoringX, setIsWideMonitoringX] = useState(existingIsWideMonitoring);
    const [isWideMonitoringTiktok, setIsWideMonitoringTiktok] = useState(existingIsWideMonitoringTiktok);
    const [isWideMonitoringInstagram, setIsWideMonitoringInstagram] = useState(existingIsWideMonitoringInstagram);
    const [visibility, setVisibility] = useState(existingVisibility);
    const [listRole, setListRole] = useState(existingListRole);
    const { activeOrg } = useOrg();
    const [error, setError] = useState('');
    const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
    const [orgMembers, setOrgMembers] = useState([]);
    const [accessList, setAccessList] = useState([]);
    const [accessLoading, setAccessLoading] = useState(false);

    useEffect(() => {
        if (visibility === 'specific' && activeOrg?.id && orgMembers.length === 0) {
            OrgManager.listMembers(activeOrg.id)
                .then(data => setOrgMembers(Array.isArray(data) ? data : (data?.members || [])))
                .catch(() => {});
        }
        if (visibility === 'specific' && isForEditing && listId && accessList.length === 0) {
            setAccessLoading(true);
            OrgManager.getListAccess(listId)
                .then(data => setAccessList((data || []).map(m => m.listener_id)))
                .catch(() => {})
                .finally(() => setAccessLoading(false));
        }
    }, [visibility, activeOrg?.id, isForEditing, listId]);

    useEffect(() => {
        if (!isForEditing) {
            loadSuggestedLists();
        } else {
            if (existingListName || existingListPrompt || existingListReactionStyle) {
                setTitle(existingListName);
                setPrompt(existingListPrompt);
                setReactionStyle(existingListReactionStyle);
                setIsWideMonitoringX(existingIsWideMonitoring);
                setIsWideMonitoringTiktok(existingIsWideMonitoringTiktok);
                setIsWideMonitoringInstagram(existingIsWideMonitoringInstagram);
            }
        }
    }, [isForEditing, listId, existingListName, existingListPrompt, existingListReactionStyle, existingIsWideMonitoring]);

    const loadSuggestedLists = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await ListsManager.fetchSuggestedLists();
            setSuggestedLists(result);
        } catch (err) {
            console.error("Error loading suggested lists:", err);
            setError(t('app.newList.loadSuggestedError'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!title.trim()) { alert(t('app.newList.alertFillTitle')); return; }
        if (!prompt.trim()) { alert(t('app.newList.alertFillPrompt')); return; }

        try {
            setIsLoading(true);
            let success = false;

            if (isForEditing) {
                if (!listId) {
                    console.error("Missing listId for editing");
                    alert(t('app.newList.alertMissingId'));
                    setIsLoading(false);
                    return;
                }
                success = await ListsManager.updateListSettings(listId, title.trim(), prompt.trim(), reactionStyle.trim(), isWideMonitoringX, isWideMonitoringTiktok, isWideMonitoringInstagram, visibility, listRole);
                if (success) await syncListAccess(listId);
            } else {
                success = await ListsManager.createListOnBackend(title.trim(), prompt.trim(), reactionStyle.trim(), isWideMonitoringX, isWideMonitoringTiktok, isWideMonitoringInstagram, visibility, activeOrg?.id, listRole);
                if (success && visibility === 'specific') {
                    const lists = await ListsManager.fetchLists(activeOrg?.id);
                    const created = (lists || []).find(l => l.name === title.trim());
                    if (created) await syncListAccess(created.id);
                }
            }

            setIsLoading(false);

            if (success) {
                if (onClose) {
                    onClose(true);
                } else {
                    navigate('/my-lists', { state: { refreshLists: true } });
                }
            } else {
                alert(t('app.newList.alertSaveError', { action: isForEditing ? 'update' : 'create' }));
            }
        } catch (err) {
            setIsLoading(false);
            console.error(`Error ${isForEditing ? 'updating' : 'creating'} list:`, err);
            alert(t('app.newList.alertSaveErrorGeneric', { action: isForEditing ? 'updating' : 'creating' }));
        }
    };

    const toggleAccess = (listenerId) => {
        setAccessList(prev =>
            prev.includes(listenerId) ? prev.filter(id => id !== listenerId) : [...prev, listenerId]
        );
    };

    const syncListAccess = async (savedListId) => {
        if (visibility !== 'specific') return;
        const current = await OrgManager.getListAccess(savedListId).then(d => (d || []).map(m => m.listener_id)).catch(() => []);
        const toGrant = accessList.filter(id => !current.includes(id));
        const toRevoke = current.filter(id => !accessList.includes(id));
        await Promise.all([
            ...toGrant.map(id => OrgManager.grantListAccess(savedListId, id)),
            ...toRevoke.map(id => OrgManager.revokeListAccess(savedListId, id)),
        ]);
    };

    const handleSuggestedListPress = async (list) => {
        try {
            const details = await ListsManager.fetchSuggestedListDetails(list.id);
            if (details) {
                navigate('/suggested-list-users', { state: { listDetails: details } });
            } else {
                alert(t('app.newList.alertLoadDetailsError'));
            }
        } catch (err) {
            console.error("Error fetching list details:", err);
            alert(t('app.newList.alertLoadDetailsError'));
        }
    };

    const handleGeneratePrompt = async () => {
        if (!title.trim()) { alert(t('app.newList.alertEnterName')); return; }
        setIsGeneratingPrompt(true);
        try {
            const generatedPrompt = await ListsManager.generateListPrompt(title.trim(), prompt.trim());
            if (generatedPrompt) {
                setPrompt(generatedPrompt);
            } else {
                alert(t('app.newList.alertGenerateError'));
            }
        } catch (err) {
            console.error('Error generating prompt:', err);
            alert(t('app.newList.alertGenerateError'));
        } finally {
            setIsGeneratingPrompt(false);
        }
    };

    const platforms = [
        { label: t('app.newList.xTwitter'), value: isWideMonitoringX, setter: setIsWideMonitoringX, Icon: FaTwitter, color: '#1da1f2' },
        { label: t('app.newList.tiktok'), value: isWideMonitoringTiktok, setter: setIsWideMonitoringTiktok, Icon: FaTiktok, color: '#ff0050' },
        { label: t('app.newList.instagram'), value: isWideMonitoringInstagram, setter: setIsWideMonitoringInstagram, Icon: FaInstagram, color: '#e1306c' },
    ];

    const visibilityOptions = [
        { value: 'private', label: t('app.newList.visibilityPrivate'), desc: t('app.newList.visibilityPrivateDesc') },
        { value: 'specific', label: t('app.newList.visibilitySpecific'), desc: t('app.newList.visibilitySpecificDesc') },
        { value: 'org', label: t('app.newList.visibilityOrg'), desc: t('app.newList.visibilityOrgDesc') },
    ];

    const css = `
        .nl-toggle:hover { opacity: 0.85; }
        .nl-vis-opt:hover { border-color: ${T.primary}60 !important; }
        .nl-member:hover { background: ${T.surface} !important; }
        .nl-suggest:hover { background: ${T.surface} !important; transform: translateY(-2px); }
        .nl-save:hover:not(:disabled) { filter: brightness(1.1); }
        .nl-gen:hover:not(:disabled) { filter: brightness(1.1); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
    `;

    const inputStyle = {
        width: '100%', boxSizing: 'border-box',
        backgroundColor: T.surface,
        border: `1px solid ${T.border}`, borderRadius: 10,
        padding: '12px 14px', color: T.text, fontSize: 14,
        outline: 'none', fontFamily: 'inherit',
        transition: 'border-color 0.15s',
    };

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />
            <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                overflow: 'hidden', background: T.bg,
            }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 40px' }}>

                    {/* Title */}
                    <div style={{ marginBottom: 28 }}>
                        <div style={{ color: T.text, fontSize: 22, fontWeight: 800, letterSpacing: -0.5, marginBottom: 4 }}>
                            {isForEditing ? t('app.newList.editTitle') : t('app.newList.createTitle')}
                        </div>
                        <div style={{ color: T.textMuted, fontSize: 13 }}>
                            {isForEditing
                                ? t('app.newList.pageHeaderEdit', { name: existingListName || title })
                                : t('app.newList.pageHeaderCreate')}
                        </div>
                    </div>

                    {/* List Name */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                            {t('app.newList.listTitle')}
                        </div>
                        <input
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder={t('app.newList.titlePlaceholder')}
                            style={inputStyle}
                        />
                    </div>

                    {/* Prompt */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('app.newList.listPrompt')}
                            </div>
                            <button
                                className="nl-gen"
                                onClick={handleGeneratePrompt}
                                disabled={!title.trim() || isGeneratingPrompt}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '5px 12px', borderRadius: 8,
                                    background: `linear-gradient(135deg, #8b5cf6, ${T.primary})`,
                                    border: 'none', cursor: 'pointer',
                                    opacity: (!title.trim() || isGeneratingPrompt) ? 0.4 : 1,
                                    transition: 'all 0.15s',
                                }}
                            >
                                {isGeneratingPrompt
                                    ? <ActivityIndicator size="small" color="#fff" />
                                    : <FaMagic size={10} color="#fff" />}
                                <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                                    {t('app.newList.generateWithAI')}
                                </span>
                            </button>
                        </div>
                        <textarea
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                            placeholder={t('app.newList.promptPlaceholder')}
                            rows={4}
                            style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
                        />
                    </div>

                    {/* Reaction Style */}
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                            {t('app.newList.reactionStyle')}
                        </div>
                        <textarea
                            value={reactionStyle}
                            onChange={e => setReactionStyle(e.target.value)}
                            placeholder={t('app.newList.reactionStylePlaceholder')}
                            rows={3}
                            style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
                        />
                    </div>

                    {/* List Role */}
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                            {t('app.newList.listRole') || 'List Role'}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {[
                                { value: 'brand', label: t('app.newList.listRoleBrand') || 'Our Brand', desc: t('app.newList.listRoleBrandDesc') || 'Monitors your brand', Icon: FaShieldAlt, color: '#0a84ff' },
                                { value: 'competitor', label: t('app.newList.listRoleCompetitor') || 'Competitor', desc: t('app.newList.listRoleCompetitorDesc') || 'Monitors a competitor', Icon: FaExchangeAlt, color: '#ff453a' },
                                { value: 'general', label: t('app.newList.listRoleGeneral') || 'General', desc: t('app.newList.listRoleGeneralDesc') || 'General monitoring', Icon: FaGlobe, color: '#8e8e93' },
                            ].map(opt => (
                                <div
                                    key={opt.value}
                                    className="nl-role-opt"
                                    onClick={() => setListRole(opt.value)}
                                    style={{
                                        flex: 1, textAlign: 'center', padding: '12px 8px',
                                        borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                                        border: `1px solid ${listRole === opt.value ? `${opt.color}70` : T.border}`,
                                        background: listRole === opt.value ? `${opt.color}12` : T.card,
                                    }}
                                >
                                    <opt.Icon size={16} color={listRole === opt.value ? opt.color : T.textMuted} style={{ marginBottom: 6 }} />
                                    <div style={{ color: listRole === opt.value ? opt.color : T.text, fontWeight: 700, fontSize: 12 }}>
                                        {opt.label}
                                    </div>
                                    <div style={{ color: T.textMuted, fontSize: 10, marginTop: 2 }}>{opt.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Wide Monitoring */}
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                            {t('app.newList.wideMonitoring')}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {platforms.map(({ label, value, setter, Icon, color }) => (
                                <div
                                    key={label}
                                    className="nl-toggle"
                                    onClick={() => setter(prev => !prev)}
                                    style={{
                                        flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                                        background: value ? `${color}12` : T.card,
                                        border: `1px solid ${value ? `${color}40` : T.border}`,
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    <div style={{
                                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                        background: `${color}18`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon size={14} color={color} />
                                    </div>
                                    <span style={{ color: T.text, fontSize: 12, fontWeight: 600, flex: 1 }}>{label}</span>
                                    <div style={{
                                        width: 38, height: 20, borderRadius: 10, flexShrink: 0,
                                        background: value ? color : 'rgba(128,128,128,0.25)',
                                        position: 'relative', transition: 'background 0.2s',
                                    }}>
                                        <div style={{
                                            position: 'absolute', top: 2, left: value ? 20 : 2,
                                            width: 16, height: 16, borderRadius: 8, background: '#fff',
                                            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Visibility */}
                    <div style={{ marginBottom: visibility === 'specific' ? 16 : 24 }}>
                        <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                            {t('app.newList.visibility')}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {visibilityOptions.map(opt => (
                                <div
                                    key={opt.value}
                                    className="nl-vis-opt"
                                    onClick={() => setVisibility(opt.value)}
                                    style={{
                                        flex: 1, textAlign: 'center', padding: '10px 8px',
                                        borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                                        border: `1px solid ${visibility === opt.value ? `${T.primary}70` : T.border}`,
                                        background: visibility === opt.value ? `${T.primary}12` : T.card,
                                    }}
                                >
                                    <div style={{ color: visibility === opt.value ? T.primary : T.text, fontWeight: 700, fontSize: 13 }}>
                                        {opt.label}
                                    </div>
                                    <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{opt.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Member picker */}
                    {visibility === 'specific' && (
                        <div style={{
                            borderRadius: 12, border: `1px solid ${T.primary}30`,
                            background: T.card, padding: 14, marginBottom: 24,
                        }}>
                            <div style={{ color: T.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                {t('app.newList.whoCanAccess')}
                            </div>
                            {accessLoading ? (
                                <ActivityIndicator size="small" color={T.primary} />
                            ) : orgMembers.length === 0 ? (
                                <div style={{ color: T.textMuted, fontSize: 12, padding: '8px 0' }}>{t('app.newList.noOtherMembers')}</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {orgMembers.map(member => {
                                        const memberId = member.listener_id || member.id;
                                        const isGranted = accessList.includes(memberId);
                                        return (
                                            <div
                                                key={memberId}
                                                className="nl-member"
                                                onClick={() => toggleAccess(memberId)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                                    background: isGranted ? `${T.primary}10` : 'transparent',
                                                    border: `1px solid ${isGranted ? `${T.primary}30` : T.border}`,
                                                    transition: 'all 0.12s',
                                                }}
                                            >
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                                                    background: isGranted ? `${T.primary}20` : 'rgba(128,128,128,0.12)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <span style={{ color: isGranted ? T.primary : T.textMuted, fontSize: 12, fontWeight: 700 }}>
                                                        {(member.username || member.email || '?')[0].toUpperCase()}
                                                    </span>
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ color: T.text, fontSize: 12, fontWeight: 600 }}>{member.username || member.email}</div>
                                                    <div style={{ color: T.textMuted, fontSize: 10 }}>{member.role}</div>
                                                </div>
                                                <div style={{
                                                    width: 20, height: 20, borderRadius: 10, flexShrink: 0,
                                                    background: isGranted ? T.primary : 'rgba(128,128,128,0.12)',
                                                    border: `1px solid ${isGranted ? T.primary : T.border}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {isGranted && <FaCheck size={9} color="#fff" />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Save Button */}
                    <button
                        className="nl-save"
                        onClick={handleSave}
                        disabled={isLoading}
                        style={{
                            width: '100%', padding: '13px 0', borderRadius: 12,
                            background: T.primary, border: 'none',
                            color: '#fff', fontSize: 14, fontWeight: 700,
                            cursor: 'pointer', transition: 'all 0.15s',
                            opacity: isLoading ? 0.6 : 1,
                            boxShadow: `0 4px 14px ${T.primary}40`,
                            fontFamily: 'inherit',
                        }}
                    >
                        {isLoading
                            ? <ActivityIndicator size="small" color="#fff" />
                            : (isForEditing ? t('app.newList.saveChanges') : t('app.newList.createList'))}
                    </button>

                    {/* Suggested Lists */}
                    {!isForEditing && (
                        <div style={{ marginTop: 36 }}>
                            <div style={{ color: T.textSec, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                                {t('app.newList.suggestedLists')}
                            </div>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                                    <ActivityIndicator size="small" color={T.primary} />
                                </div>
                            ) : suggestedLists.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    {suggestedLists.map(item => (
                                        <div
                                            key={item.id}
                                            className="nl-suggest"
                                            onClick={() => handleSuggestedListPress(item)}
                                            style={{
                                                width: 'calc(50% - 5px)', borderRadius: 12,
                                                background: T.card, border: `1px solid ${T.border}`,
                                                padding: 14, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 12,
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            {item.list_image_url ? (
                                                <img
                                                    src={item.list_image_url}
                                                    alt=""
                                                    style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                                    background: `${T.primary}12`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 18,
                                                }}>
                                                    📋
                                                </div>
                                            )}
                                            <div style={{
                                                color: T.text, fontSize: 13, fontWeight: 600,
                                                overflow: 'hidden', display: '-webkit-box',
                                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            }}>
                                                {item.list_name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center', padding: '20px 16px',
                                    background: T.card, borderRadius: 12, border: `1px solid ${T.border}`,
                                    color: T.textMuted, fontSize: 13,
                                }}>
                                    {t('app.newList.noSuggestedLists')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default NewListScreen;
